/**
 * Firestore service for GIF theme packs.
 *
 * Super Admins save global packs that all users can browse and apply.
 * Collection: `gifThemePacks`
 */
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { GifEntry } from '@/contexts/theme-context';

export interface GifThemePack {
  id:          string;
  name:        string;
  description: string;
  gifs:        GifEntry[];
  /** uid of the super-admin who created it */
  createdBy:   string;
  createdAt:   Timestamp;
  /** When true, all users can see and apply this pack */
  isPublic:    boolean;
}

const COL = 'gifThemePacks';

export async function getPublicGifPacks(): Promise<GifThemePack[]> {
  try {
    const q = query(
      collection(db, COL),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as GifThemePack));
  } catch {
    return [];
  }
}

export async function getAllGifPacks(): Promise<GifThemePack[]> {
  try {
    const snap = await getDocs(query(collection(db, COL), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as GifThemePack));
  } catch {
    return [];
  }
}

export async function saveGifPack(
  pack: Omit<GifThemePack, 'id' | 'createdAt'>
): Promise<string> {
  const docRef = await addDoc(collection(db, COL), {
    ...pack,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function deleteGifPack(id: string): Promise<void> {
  await deleteDoc(doc(db, COL, id));
}
